import React, { useCallback, useEffect, useState } from 'react';
import { Wrapper } from './style';
import { DatePicker, message, Select, Alert, Button, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { MakeApiCall } from '../../../apis';
import AllAlerts from './lib/allAlerts';
import EmailAlerts from './lib/alerts';
import { GetMarketplaceDropdown, GetMarketplaceList, rangePresets } from '../../../config';
import Settings from './lib/settings';
import AddNewAlerts from './lib/AddNewAlerts';
import { Helmet } from 'react-helmet';
import dayjs from 'dayjs';
import SyncToast from "../../../components/toast/SyncToast";
import SetupStepsBanner from "../../../components/onboarding/Setupstepsbanner";
import FeatureLockedOverlay from "../../../components/modal/Featurelockedoverlay";
import { FilterOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';

const { Option } = Select;
const { RangePicker } = DatePicker;

const AMAZON_DATE_FORMAT = 'YYYY-MM-DD';

function Alerts({ userData }) {
    // --- Connection Logic ---
    const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
    const isDataSyncing = isSpApiConnected && userData?.user?.user_data?.is_data_sync_email_sent === null;
    const trialExpired = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);

    const [activeTab, setActiveTab] = useState('allAlerts');
    const [totalPage, setTotalPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);

    const [allAlerts, setAllAlerts] = useState([]);
    const [alerts, setAlerts] = useState([]);

    const [marketplaceList, setMarketplaceList] = useState([]);
    const [selectedMarketplace, setSelectedMarketplace] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [currencyList, setCurrencyList] = useState([]);
    const [selectedCurrencySign, setSelectedCurrencySign] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [asinList, setAsinList] = useState([]);
    const [selectedAsins, setSelectedAsins] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSeverity, setSelectedSeverity] = useState(null);

    // Default date range: last 30 days
    const [selectedDateRange, setSelectedDateRange] = useState([
        dayjs().subtract(29, 'day'),
        dayjs(),
    ]);

    const [editing, setEditing] = useState(null);
    const [fetchAlertSettings, setFetchAlertSettings] = useState(() => () => {});

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchText, setSearchText] = useState('');

    // Sync toast dismissed state — resets if sync restarts
    const [syncToastDismissed, setSyncToastDismissed] = useState(false);

    const [filters, setFilters] = useState({
        page: 1,
        'per-page': 20,
        name: '',
        marketplace_id: null,
    });

    const [alertCategories, setAlertCategories] = useState([]);
    const [alertDataMap, setAlertDataMap] = useState({
        types: {},
        receive_on: [],
        logicTypeMap: {}
    });

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset dismissed state if a new sync cycle begins
    useEffect(() => {
        if (isDataSyncing) setSyncToastDismissed(false);
    }, [isDataSyncing]);

    const fetchProductList = async (search = '', pageNum = 1, append = false) => {
        console.log("Fetching products with search:", search, "page:", pageNum, "append:", append);
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (!isSpApiConnected || trialExpiredLocal) return;

        setIsSearching(true);
        try {
            const url = `get-products?page=${pageNum}&per-page=10${search ? `&search_text=${search}` : ''}`;
            const response = await MakeApiCall(url, 'get', null, true, {}, userData?.token);

            if (response?.status) {
                const records = response?.data?.records || [];
                const formattedData = records.map(item => ({
                    asin: item.asin,
                    name: item.item_name,
                    image: item.item_image,
                    sku: item.sku,
                }));

                setAsinList(prev => append ? [...prev, ...formattedData] : formattedData);
                setHasMore(records.length === 10);
                setPage(pageNum);
            }
        } catch (error) {
            message.error('Error fetching products.');
        } finally {
            setIsSearching(false);
        }
    };

    const handlePopupScroll = useCallback((e) => {
        const { target } = e;
        const isAtBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 10;

        if (isAtBottom && !isSearching && hasMore) {
            setPage((prevPage) => {
                const nextPage = prevPage + 1;
                fetchProductList(searchText, nextPage, true);
                return nextPage;
            });
        }
    }, [isSearching, hasMore, searchText, fetchProductList]);

    const debouncedSearch = useCallback(
        (() => {
            let timeout;
            return (val) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => {
                    const searchVal = val || '';
                    setSearchText(searchVal);
                    setPage(1);
                    setHasMore(true);
                    fetchProductList(searchVal, 1, false);
                }, 500);
            };
        })(), [userData?.token, isSpApiConnected]
    );

    const GetMarketplace = async () => {
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (!isSpApiConnected || trialExpiredLocal) return;
        const marketplace = await GetMarketplaceDropdown();

        if (marketplace?.marketplace_list?.length > 0) {
            const selectedMarketplaceEntry = Object.entries(marketplace.selected_marketplace || {})[0];
            const [marketplaceId] = selectedMarketplaceEntry || [];

            const firstMarketplace = marketplace.marketplace_list.find(
                (m) => m.id === marketplaceId
            );

            setMarketplaceList(marketplace.marketplace_list);
            setSelectedMarketplace(firstMarketplace);

            setFilters((prev) => ({
                ...prev,
                marketplace_id: marketplaceId,
            }));
        } else {
            setLoading(false);
        }
    };

    const fetchAlertMaster = async () => {
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (!isSpApiConnected || trialExpiredLocal) return;
        setLoading(true);
        try {
            const response = await MakeApiCall(
                "get-alert-master-data",
                "get",
                null,
                true,
                {},
                userData?.token
            );
            if (response?.status) {
                const { alert_catalogs_data, alert_type, alert_receive_on, alert_logic_type } = response.data;

                const categories = Object.entries(alert_catalogs_data).map(([key, label]) => ({
                    label,
                    value: key
                }));

                setAlertCategories(categories);
                setAlertDataMap({
                    types: alert_type,
                    receive_on: alert_receive_on,
                    logicTypeMap: alert_logic_type
                });
            } else {
                message.warning(response?.message || "Failed to load alert data");
            }
        } catch (error) {
            message.error("Error fetching alert master data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSpApiConnected) {
            GetMarketplace();
            fetchProductList('', 1, false);
            fetchAlertMaster();
        } else {
            setLoading(false);
        }
    }, [isSpApiConnected]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const response = await MakeApiCall(
                editing ? `update-alert/${editing.id}` : "add-alert",
                editing ? "post" : "post",
                values,
                true,
                {},
                userData?.token
            );
            if (response?.status) {
                message.success(editing ? "Alert updated successfully!" : "Alert added successfully!");
                setIsModalOpen(false);
                setActiveTab('settings');
                fetchAlertSettings();
            } else {
                message.error(response?.message || (editing ? "Failed to update alert." : "Failed to add alert."));
            }
        } catch (error) {
            console.error("Error adding Alert:", error);
            message.error("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    // Count active filters
    const getActiveFiltersCount = () => {
        let count = 0;
        if (selectedAsins && selectedAsins.length > 0) count++;
        if (selectedCategory) count++;
        if (selectedSeverity) count++;
        if (selectedMarketplace?.id) count++;
        if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) count++;
        return count;
    };

    const activeFiltersCount = getActiveFiltersCount();

    return (
        <Wrapper>
            <Helmet><title>Alerts</title></Helmet>
            <SetupStepsBanner userData={userData} />

            {/* Floating Sync Toast — replaces the inline Alert for data syncing */}
            <SyncToast
                visible={isDataSyncing && !syncToastDismissed}
                onDismiss={() => setSyncToastDismissed(true)}
            />

            <div style={{ position: 'relative' }}>
                <FeatureLockedOverlay locked={trialExpired || !isSpApiConnected} title="Features Locked" message={trialExpired ? "Your trial has expired. Please renew your subscription to continue." : "Connect your Amazon account to unlock analytics."}/>
                <div className="mb-5 mx-md-n9 mx-n2 px-10 py-5 bg-white filter">
                    <div className="position-relative d-flex wrap justify-content-between">
                        <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 d-inline-flex">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === 'allAlerts' ? 'active' : ''}`}
                                    onClick={() => isSpApiConnected && setActiveTab('allAlerts')}
                                >
                                    Alert History
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                                    onClick={() => isSpApiConnected && setActiveTab('settings')}
                                >
                                    Alert Rules
                                </button>
                            </li>
                        </ul>
                        <button
                            className="btn btn-danger fs-7 px-md-5 px-3 fw-bold"
                            disabled={!isSpApiConnected}
                            onClick={() => { setEditing(null); setIsModalOpen(true); }}
                        >
                            Add New Alerts
                        </button>
                    </div>
                </div>

                {/* Filters Section with Expand/Collapse for Mobile */}
                <div className="mb-5 mx-md-n9 mx-0  px-md-10 px-0 filters-section filters">
                    {/* Mobile Filter Toggle Button */}
                    {isMobile && (
                        <button
                            className="filter-toggle-btn"
                            onClick={() => setFiltersExpanded(!filtersExpanded)}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                marginBottom: filtersExpanded ? '6px' : '0'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FilterOutlined />
                                <span style={{ fontWeight: 500 }}>Filters</span>
                                {activeFiltersCount > 0 && (
                                    <span style={{
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        borderRadius: '12px',
                                        padding: '2px 8px',
                                        fontSize: '12px',
                                        marginLeft: '4px'
                                    }}>
                                        {activeFiltersCount}
                                    </span>
                                )}
                            </div>
                            {filtersExpanded ? <UpOutlined /> : <DownOutlined />}
                        </button>
                    )}

                    {/* Filters Content */}
                    <div 
                        className=" gap-4 filters" 
                        style={{ 
                            overflowX: isMobile ? 'visible' : 'auto',
                            flexWrap: isMobile ? 'wrap' : 'nowrap',
                            whiteSpace: isMobile ? 'normal' : 'nowrap',
                            display: isMobile && !filtersExpanded ? 'none' : 'flex',
                            padding: isMobile ? '16px 0' : '0',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}
                    >
                        <div className="d-flex align-items-center gap-4 w-100" style={{ 
                            gap: '1rem', 
                            flexWrap: isMobile ? 'wrap' : 'nowrap', 
                            minWidth: 0, 
                            whiteSpace: isMobile ? 'normal' : 'nowrap',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            {/* Product Search */}
                            <div style={{ flex: isMobile ? '1 1 100%' : '1 1 40%', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
                                
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder="Search or select products"
                                    size="large"
                                    value={selectedAsins}
                                    onChange={setSelectedAsins}
                                    onSearch={debouncedSearch}
                                    onPopupScroll={handlePopupScroll}
                                    loading={isSearching && isSpApiConnected}
                                    disabled={!isSpApiConnected}
                                    filterOption={false}
                                    optionLabelProp="value"
                                    maxTagCount="responsive"
                                    notFoundContent={isSearching ? <div className="text-center p-2"><Spin size="small" /></div> : null}
                                    style={{ width: '100%', minWidth: 0 }}
                                >
                                    {asinList.map(item => (
                                        <Option key={item.asin} value={item.asin}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <img
                                                    src={item.image || 'https://app.sellerview.ai/sellerview_api/images/no-product-image.png'}
                                                    alt={item.name}
                                                    className="w-50px"
                                                />
                                                <div>
                                                    <div style={{ fontWeight: '500' }}>{item.name}</div>
                                                    <div style={{ fontSize: '13px', color: '#888' }}>{item.asin}</div>
                                                    <div style={{ fontSize: '13px', color: '#888' }}>{item.sku}</div>
                                                </div>
                                            </div>
                                        </Option>
                                    ))}
                                    {isSearching && hasMore && (
                                        <Option disabled key="loading-more" className="text-center bg-light">
                                            <Spin size="small" /> <small className="ms-2">Loading more...</small>
                                        </Option>
                                    )}
                                </Select>
                            </div>

                            {/* Category */}
                            <div style={{ flex: isMobile ? '1 1 100%' : '0 0 15%', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
                                
                                <Select
                                    placeholder="Select Category"
                                    size="large"
                                    loading={loading && alertCategories.length === 0}
                                    allowClear
                                    disabled={!isSpApiConnected}
                                    value={selectedCategory}
                                    onChange={(val) => setSelectedCategory(val || null)}
                                    style={{ width: '100%' }}
                                >
                                    {alertCategories.map(cat => (
                                        <Option key={cat.value} value={cat.value}>
                                            {cat.label}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            {/* Severity */}
                            <div style={{ flex: isMobile ? '1 1 100%' : '0 0 15%', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
                                
                                <Select
                                    placeholder="Select Severity"
                                    size="large"
                                    allowClear
                                    disabled={!isSpApiConnected}
                                    value={selectedSeverity}
                                    onChange={(val) => setSelectedSeverity(val || null)}
                                    style={{ width: '100%' }}
                                >
                                    <Option value="Minor">Minor</Option>
                                    <Option value="Major">Major</Option>
                                    <Option value="Critical">Critical</Option>
                                </Select>
                            </div>

                            {/* Marketplace */}
                            <div style={{ flex: isMobile ? '1 1 100%' : '0 0 15%', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
                               
                                <Select
                                    size="large"
                                    placeholder="Select Marketplace"
                                    options={GetMarketplaceList(marketplaceList)}
                                    value={selectedMarketplace?.id}
                                    disabled={!isSpApiConnected}
                                    onChange={(id) => {
                                        const selected = marketplaceList.find((m) => m.id === id);
                                        setSelectedMarketplace(selected);
                                        setFilters({ ...filters, marketplace_id: id });
                                        setLoading(true);
                                    }}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Date Range — with presets & default last 30 days */}
                            <div style={{ flex: isMobile ? '1 1 100%' : '0 0 15%', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
                                
                                <RangePicker
                                    size="large"
                                    disabled={!isSpApiConnected}
                                    presets={rangePresets}
                                    value={selectedDateRange}
                                    onChange={(dates) => setSelectedDateRange(dates || [])}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Optional: Clear Filters Button for Mobile */}
                    {isMobile && filtersExpanded && activeFiltersCount > 0 && (
                        <div style={{ marginTop: '12px', textAlign: 'right' }}>
                            <button
                                onClick={() => {
                                    setSelectedAsins([]);
                                    setSelectedCategory(null);
                                    setSelectedSeverity(null);
                                    setSelectedMarketplace(null);
                                    setSelectedDateRange([dayjs().subtract(29, 'day'), dayjs()]);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#dc3545',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '4px 8px'
                                }}
                            >
                                Clear all filters
                            </button>
                        </div>
                    )}
                </div>

                {activeTab === 'allAlerts' && (
                    <AllAlerts
                        userData={userData}
                        list={allAlerts}
                        loading={loading}
                        selectedAsins={selectedAsins}
                        selectedCategory={selectedCategory}
                        selectedSeverity={selectedSeverity}
                        selectedDateRange={selectedDateRange}
                        onCategoryChange={setSelectedCategory}
                    />
                )}
                {activeTab === 'alerts' && <EmailAlerts list={alerts} loading={loading} />}
                {activeTab === 'settings' && (
                    <Settings
                        exposeFetcher={setFetchAlertSettings}
                        setIsModalOpen={setIsModalOpen}
                        setEditing={setEditing}
                        userData={userData}
                        loading={loading}
                    />
                )}

                <AddNewAlerts
                    isModalVisible={isModalOpen}
                    handleCancel={() => { setIsModalOpen(false); setEditing(null); }}
                    handleSubmit={async (values) => {
                        await handleSubmit(values);
                        setEditing(null);
                    }}
                    marketplaceList={marketplaceList}
                    editing={editing}
                    setSelectedAsins={setSelectedAsins}
                    selectedAsins={selectedAsins}
                    asinList={asinList}
                    userData={userData}
                    alertCategories={alertCategories}
                    alertDataMap={alertDataMap}
                    fetchProductList={fetchProductList}
                    isSearching={isSearching}
                    hasMore={hasMore}
                    page={page}
                    searchText={searchText}
                />
            </div>
        </Wrapper>
    );
}

export default Alerts;